import { useInstantFormat } from '../instant-format';
/**
 * Mail (DESIGN.md T10): message list + reading pane inside the shell content (the folder tree
 * lives in the NavRail). The list is server-paged (50 per page, more on scroll); opening a
 * message marks it read. Phones show one pane at a time.
 *
 * Every message action comes from one registry (`createMessageActions`, DESIGN.md §18): the
 * reading-pane toolbar and "…" menu, the list's context menu (right-click, Shift+F10,
 * long-press), the bulk bar for a multi-selection, the command palette and the shortcuts. That
 * includes the message tools (T10 "Message actions"): forward as attachment, message details,
 * view source, download .eml and print. Attached messages open in a nested message view; files
 * download; the attachment strip's menu comes from `createAttachmentActions`.
 *
 * Compose (T11, like OWA): the composer opens in the reading-pane column, the list stays visible.
 * Replies/forwards start with the quoted history in the editor. Opening another message while the
 * composer holds changes asks to discard first (`leaveComposer`, no drafts yet).
 */
import { useI18n } from '@platform/i18n';
import { SearchIndexFooter } from '../search-index-footer';
import {
  ActionButtons,
  ActionOverflowMenu,
  AttachmentStrip,
  BREAKPOINTS,
  buildQuotedBody,
  Button,
  cn,
  EmptyState,
  FolderPickerDialog,
  entityPickerLabelsFor,
  formatBytes,
  MailListPane,
  PaneContent,
  PaneResizeHandle,
  printMail,
  ReadingPane,
  resolveActions,
  toast,
  useActionShortcuts,
  useAsync,
  useErrorText,
  useMediaQuery,
  usePaletteActions,
  usePaneWidth,
  type AttachmentStripItem,
  type ContextMenuSpec,
  type MailListItem,
  type ReadingPaneMessage,
} from '@platform/ui';
import { ArrowLeftIcon, ListChecksIcon, RefreshCwIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAddressActions } from '../actions/address-actions';
import { createAttachmentActions } from '../actions/attachment-actions';
import { createMessageActions, type MessageTool } from '../actions/message-actions';
import { api, type Address, type Message, type MessageSummary, type SearchRequest } from '../api';
import { MeetingCard } from '../calendar/meeting-card';
import { downloadEml, startDownload } from '../download';
import { useMail, type ComposeMode } from '../mail-context';
import { useMailArrivalRefresh, useMailRefreshList, useMailSearchRefresh } from '../mail-refresh';
import { folderTransferPlan, transferSourceFolders, transferWithinStore } from '../folder-transfer';
import { replyRecipients } from '../reply';
import { looksLikeOperator } from '../search-suggest';
import { createHighlighter, searchHighlightTerms } from '../search-highlight';
import { QUICK_FILTER_KEYS } from '../search-filters';
import { SearchRefine } from '../search-refine';
import { draftFromFilter, filterFromDraft, validateSearchDraft, type SearchDraft, type SearchDraftErrors } from '../search-filter-draft';
import { SearchFilterDialog } from '../search-filter-dialog';
import { MessageDialogs, type MessageDialog } from './message-tools';
import { useRegionalSettings } from '../regional-settings';
import { useInitialMailRender } from '../use-initial-mail-render';

/** Reading pane person: name (address on hover), photo from the directory. */
const person = ({ name, address }: Address) => ({ name: name || address, address, photoUrl: api.photoUrl(address) });
/** "Name <address>" for the printed header block. */
const formatAddress = ({ name, address }: Address) => (name && name !== address ? `${name} <${address}>` : address);
const toInit = (list: readonly Address[]) => list.map(({ name, address }) => ({ email: address, name }));
const messageId = (message: MessageSummary) => message.id;
const searchMessages = (request: SearchRequest) => api.searchMessages(request);

function matches(message: MessageSummary, query: string, locale: 'en' | 'de'): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle || looksLikeOperator(needle, locale)) return true;
  return [message.subject, message.preview, message.from.name, message.from.address].some((field) => field.toLowerCase().includes(needle));
}

interface Selection {
  folderId: string;
  ids: string[];
  /** Anchor of range selection; the message shown when only one is selected. */
  activeId?: string;
  /** Opened by click/Enter/"Open" (phones then switch to the reading pane); right-click only selects. */
  opened?: boolean;
}

export function MailPage() {
  const { t, formatDate, formatNumber, locale } = useI18n();
  const formatInstant = useInstantFormat();
  const errorText = useErrorText();
  const { folderId, folders, folderItems, search, searchQuery, clearSearchQuery, searchScope, setSearchScope, searchFilter, setSearchFilter, searchPanelOpen, setSearchPanelOpen, compose, composer, composerLayout, leaveComposer, reloadFolders, selfAddresses, listVersion, setMailDrag, mailDropHandlerRef } = useMail();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const mailListPane = usePaneWidth('webmail.mailListWidth', 360);
  const loadPage = useCallback((offset: number) => api.listMessages(folderId, offset), [folderId]);
  const list = useMailRefreshList(loadPage, folderId, errorText.notify);
  const { value: regionalPreferences } = useRegionalSettings();
  const { onInitialMailRendered, initialPreferencesReady } = useMail();
  useInitialMailRender(!list.loading && !list.error && initialPreferencesReady && regionalPreferences !== undefined, onInitialMailRendered);
  const searchFilterKey = JSON.stringify(searchFilter ?? {});
  const [searchDraftState, setSearchDraftState] = useState(() => ({ key: searchFilterKey, value: draftFromFilter(searchFilter) }));
  const searchDraft = searchDraftState.key === searchFilterKey ? searchDraftState.value : draftFromFilter(searchFilter);
  const [searchDraftErrors, setSearchDraftErrors] = useState<SearchDraftErrors>({});
  const searchFallbackFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (searchPanelOpen) searchFallbackFocus.current = Array.from(document.querySelectorAll<HTMLButtonElement>('button')).find(button => button.getAttribute('aria-label') === t('webmail.searchPlaceholder')) ?? null;
  }, [searchPanelOpen, t]);
  const searching = Boolean(searchQuery.trim() || Object.keys(searchFilter ?? {}).length);
  // Counts refresh when the user switches folder; the layout already loaded the tree on first mount.
  const mountedFolderId = useRef<string>(undefined);
  useEffect(() => {
    const previous = mountedFolderId.current;
    mountedFolderId.current = folderId;
    if (previous !== undefined && previous !== folderId) reloadFolders();
  }, [folderId, reloadFolders]);
  const resetSearchDraft = (filter: SearchRequest['filter']): void => { setSearchDraftErrors({}); setSearchDraftState({ key: JSON.stringify(filter ?? {}), value: draftFromFilter(filter) }); };
  const searchRequest = useMemo<SearchRequest | undefined>(() => {
    if (!searching) return undefined;
    const scope: SearchRequest['scope'] = searchScope === 'folder'
      ? { kind: 'folder', folderId }
      : searchScope === 'folderTree'
        ? { kind: 'folderTree', folderId }
        : searchScope === 'mailbox'
          ? { kind: 'mailbox' }
          : { kind: 'allMailboxes' };
    return { scope, query: searchQuery, ...(Object.keys(searchFilter ?? {}).length && { filter: searchFilter }), locale: locale.startsWith('de') ? 'de' : 'en', pageSize: 50 };
  }, [folderId, locale, searchFilter, searchQuery, searchScope, searching]);
  const searchList = useMailSearchRefresh(searchRequest, searchMessages, errorText.notify);
  const refreshSearch = searchList.refresh;
  const refreshList = list.refresh;
  const refreshMail = useCallback(async () => {
    if (await (searching ? refreshSearch() : refreshList())) reloadFolders();
  }, [refreshList, reloadFolders, refreshSearch, searching]);
  useMailArrivalRefresh(refreshMail, true);
  const setDraft = <K extends keyof SearchDraft>(key: K, value: SearchDraft[K]) => {
    setSearchDraftErrors({});
    setSearchDraftState(current => ({ key: searchFilterKey, value: { ...(current.key === searchFilterKey ? current.value : draftFromFilter(searchFilter)), [key]: value } }));
  };
  const applySearchDraft = () => {
    const errors = validateSearchDraft(searchDraft);
    setSearchDraftErrors(errors);
    if (Object.keys(errors).length) return false;
    setSearchFilter(filterFromDraft(searchDraft));
    setSearchPanelOpen(false);
    return true;
  };
  const activeFilterChips = [
    ...(searchQuery ? [{ key: 'query', name: t('webmail.searchAdvanced.query'), shown: searchQuery }] : []),
    ...Object.entries(searchFilter ?? {}).map(([key, value]) => {
    const names: Record<string, string> = {
      subject: t('webmail.searchAdvanced.subject'), body: t('webmail.searchAdvanced.body'), from: t('webmail.searchAdvanced.from'), to: t('webmail.searchAdvanced.to'), cc: t('webmail.searchAdvanced.cc'), received: t('webmail.searchAdvanced.received'), size: t('webmail.searchAdvanced.size'),
      attachmentName: t('webmail.searchAdvanced.attachmentName'), categories: t('webmail.searchAdvanced.categories'), hasAttachments: t('webmail.searchAdvanced.hasAttachments'),
      unread: t('webmail.searchAdvanced.unread'), flagged: t('webmail.searchAdvanced.flagged'), importance: t('webmail.searchAdvanced.importance'),
    };
    let shown: string;
    if (typeof value === 'boolean') shown = t(value ? 'webmail.searchAdvanced.yes' : 'webmail.searchAdvanced.no');
    else if (Array.isArray(value)) shown = value.join(', ');
    else if (key === 'importance' && typeof value === 'string') {
      const labels = { low: t('webmail.searchAdvanced.low'), normal: t('webmail.searchAdvanced.normal'), high: t('webmail.searchAdvanced.high') };
      shown = labels[value as keyof typeof labels] ?? value;
    } else if (key === 'size' && typeof value === 'object' && value !== null) {
      const range = value as { min?: number; max?: number };
      const parts = [
        range.min !== undefined ? t('webmail.searchAdvanced.rangeMinimum', { value: formatNumber(range.min) }) : '',
        range.max !== undefined ? t('webmail.searchAdvanced.rangeMaximum', { value: formatNumber(range.max) }) : '',
      ].filter((part) => part !== '');
      shown = parts.join(' – ');
    } else if ((key === 'received' || key === 'sent') && typeof value === 'object' && value !== null) {
      const range = value as { from?: string; to?: string };
      const parts = [
        range.from ? t('webmail.searchAdvanced.rangeFrom', { value: formatDate(range.from, { dateStyle: 'short' }) }) : '',
        range.to ? t('webmail.searchAdvanced.rangeTo', { value: formatDate(range.to, { dateStyle: 'short' }) }) : '',
      ].filter((part) => part !== '');
      shown = parts.join(' – ');
    } else shown = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
      return { key, name: names[key] ?? key, shown };
    }),
  ];
  const removeSearchFilter = (key: string) => {
    if (key === 'query') { clearSearchQuery(); return; }
    const next = { ...(searchFilter ?? {}) } as Record<string, unknown>;
    delete next[key];
    setSearchFilter(next);
  };
  const searchResult = searchList.page;
  const searchCursor = searchResult?.cursor ?? null;
  const searchFailed = searchList.failed;
  const searchLoading = searchList.busy;
  const loadMoreSearch = searchList.loadMore;
  const searchHasNoMatches = Boolean(searching && searchResult?.complete && searchResult.skipped.length === 0 && searchResult.items.length === 0);
  const [selection, setSelection] = useState<Selection>();
  const [picker, setPicker] = useState<{ mode: 'move' | 'copy'; ids: string[]; sourceFolderIds: string[] }>();
  const transferPlan = useMemo(() => folderTransferPlan(folderItems, picker?.sourceFolderIds ?? []), [folderItems, picker?.sourceFolderIds]);
  const [remoteAllowed, setRemoteAllowed] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<{ id: string; subject: string; kind: MessageDialog }>();
  const [nested, setNested] = useState<{ messageId: string; attachmentId: string }>();

  // Explicit folder activation and sends refresh the current view, including an active search.
  const lastListVersion = useRef(listVersion);
  useEffect(() => {
    if (lastListVersion.current === listVersion) return;
    lastListVersion.current = listVersion;
    void refreshMail();
  }, [listVersion, refreshMail]);

  // Selection belongs to a folder; switching folders clears it without an effect.
  const current = selection?.folderId === folderId ? selection : undefined;
  const selectedIds = useMemo(() => current?.ids ?? [], [current]);
  const readingId = selectedIds.length === 1 ? selectedIds[0] : undefined;
  const message = useAsync(() => (readingId ? api.getMessage(readingId) : Promise.resolve(undefined)), readingId ?? '');
  const open: Message | undefined = message.data?.id === readingId ? message.data : undefined;

  // The nested view belongs to the message in the reading pane.
  const openAttachment = nested && nested.messageId === readingId ? nested : undefined;
  const attached = useAsync(
    () => (openAttachment ? api.getAttachedMessage(openAttachment.messageId, openAttachment.attachmentId) : Promise.resolve(undefined)),
    openAttachment ? `${openAttachment.messageId}/${openAttachment.attachmentId}` : '',
  );

  const baseVisible = useMemo(() => searching ? (searchResult?.items ?? []) : list.items.filter((item) => matches(item, search, locale.startsWith('de') ? 'de' : 'en')), [list.items, locale, search, searchResult?.items, searching]);
  const summariesOf = useCallback((ids: readonly string[]) => ids.flatMap((id) => baseVisible.filter((item) => item.id === id)), [baseVisible]);
  const selected = useMemo(() => summariesOf(selectedIds), [summariesOf, selectedIds]);
  const visible = baseVisible;
  // Matched words are marked in results and in a message opened from them, never outside a search.
  const highlighter = useMemo(
    () => (searching ? createHighlighter(searchHighlightTerms(searchQuery, searchFilter, locale.startsWith('de') ? 'de' : 'en')) : undefined),
    [locale, searchFilter, searchQuery, searching],
  );
  const readerHighlighter = highlighter && selectedIds.length === 1 && baseVisible.some((item) => item.id === selectedIds[0]) ? highlighter : undefined;
  const items = useMemo<MailListItem[]>(
    () =>
      visible.map((item) => ({
        id: item.id,
        from: item.from.name || item.from.address,
        fromAddress: item.from.address,
        subject: item.subject,
        preview: item.preview,
        dateLabel: item.receivedAt ? formatInstant(item.receivedAt, { dateStyle: 'short', timeStyle: 'short' }) : '',
        unread: item.unread,
        flagged: item.flagged,
        hasAttachments: item.hasAttachments,
      })),
    [visible, formatInstant],
  );

  /** Optimistic update of list rows; reverts and reports when the server call fails. */
  const update = (targets: readonly MessageSummary[], change: Partial<MessageSummary>, call: () => Promise<void>) => {
    for (const target of targets) list.replace({ ...target, ...change });
    call().then(reloadFolders, (error: unknown) => {
      for (const target of targets) list.replace(target);
      errorText.notify(error);
    });
  };

  const setRead = (targets: readonly MessageSummary[], read: boolean) => {
    const changed = targets.filter((target) => target.unread === read);
    if (changed.length > 0) update(changed, { unread: !read }, () => Promise.all(changed.map((target) => api.setRead(target.id, read))).then(() => undefined));
  };

  const setFlag = (targets: readonly MessageSummary[], flagged: boolean) => {
    const setFlags = api.setFlag;
    if (setFlags) update(targets, { flagged }, () => setFlags(targets.map(messageId), flagged));
  };

  const select = (id: string) => {
    if (folders.find(folder => folder.id === folderId)?.wellKnown === 'drafts') {
      api.getDraft(id).then(draft => compose({ mode: 'draft', draft: { id: draft.id, version: draft.version, savedAt: draft.savedAt },
        initial: { ...(draft.from && { from: draft.from.email }), to: draft.to.map(a => ({ name: a.name ?? '', email: a.email })), cc: draft.cc.map(a => ({ name: a.name ?? '', email: a.email })), bcc: draft.bcc.map(a => ({ name: a.name ?? '', email: a.email })), subject: draft.subject, body: { format: draft.body.type, content: draft.body.content } },
        attachments: draft.attachments.map(a => ({ id: `draft-${a.id}`, source: 'draft', ref: a.id, name: a.name, kind: a.kind === 'item' ? 'message' : 'file', size: a.size })),
      })).catch(errorText.notify);
      return;
    }
    const openMessage = () => {
      setSelection({ folderId, ids: [id], activeId: id, opened: true });
      setRead(summariesOf([id]), true);
    };
    // A composer in the reading pane gives way to the opened message (asks first when it holds changes).
    if (composerLayout === 'pane') leaveComposer(openMessage);
    else openMessage();
  };

  // Ctrl/Shift selection and right-click: no mark-as-read (Outlook marks on open only).
  const changeSelection = (ids: string[]) => setSelection({ folderId, ids, activeId: ids.length === 1 ? ids[0] : (current?.activeId ?? ids[0]) });

  const prefixed = (prefixKey: 'webmail.reader.forwardPrefix' | 'webmail.reader.replyPrefix', subject: string) => {
    const prefix = t(prefixKey);
    return subject.toLowerCase().startsWith(prefix.toLowerCase()) ? subject : `${prefix} ${subject}`;
  };

  /** The full message of a summary: the open one, or loaded on demand (list, context menu). */
  const load = async (summary: MessageSummary): Promise<Message | undefined> => {
    if (open?.id === summary.id) return open;
    try {
      return await api.getMessage(summary.id);
    } catch (error) {
      errorText.notify(error);
      return undefined;
    }
  };

  const dateLabel = (iso: string) => (iso ? formatInstant(iso, { dateStyle: 'full', timeStyle: 'short' }) : '');

  /** Editor content of a reply/forward: cursor line, then the quoted history (header in the user's language). */
  const quotedBody = (source: Message) =>
    buildQuotedBody({
      header: [
        [t('webmail.compose.quote.from'), formatAddress(source.from)],
        [t('webmail.compose.quote.sent'), dateLabel(source.sentAt ?? source.receivedAt)],
        [t('webmail.compose.quote.to'), source.to.map(formatAddress).join('; ')],
        ...(source.cc.length > 0 ? [[t('webmail.compose.quote.cc'), source.cc.map(formatAddress).join('; ')] as const] : []),
        [t('webmail.compose.quote.subject'), source.subject],
      ],
      body: source.text !== undefined ? { format: 'text', content: source.text } : { format: 'html', content: source.html },
      allowRemote: remoteAllowed.has(source.id),
      inlineImageUrl: (contentId) => api.inlineImageUrl(source.id, contentId),
    });

  const respond = async (mode: Exclude<ComposeMode, 'new'>, summary: MessageSummary) => {
    if (mode === 'forwardAsAttachment') {
      // Like Outlook: a new message with the original attached as .eml (no body needed).
      compose({
        mode,
        initial: { subject: prefixed('webmail.reader.forwardPrefix', summary.subject) },
        attachments: [{ id: `item-${summary.id}`, source: 'item', kind: 'message', name: summary.subject || t('webmail.list.noSubject'), ref: summary.id }],
      });
      return;
    }
    const source = await load(summary);
    if (!source) return;
    const subject = prefixed(mode === 'forward' ? 'webmail.reader.forwardPrefix' : 'webmail.reader.replyPrefix', source.subject);
    const recipients = mode === 'forward' ? { to: [], cc: [] } : replyRecipients(source, mode === 'replyAll', selfAddresses);
    // Forward carries the original's attachments as removable chips (pictures shown in the text go along inline).
    const originals =
      mode === 'forward'
        ? source.attachments.map((a) => ({ id: `original-${a.id}`, source: 'original' as const, kind: a.kind === 'item' ? ('message' as const) : ('file' as const), name: a.name, size: a.size, ref: a.id }))
        : [];
    compose({
      mode,
      sourceId: source.id,
      initial: { to: toInit(recipients.to), cc: toInit(recipients.cc), subject, body: { format: 'html', content: quotedBody(source) } },
      attachments: originals,
    });
  };

  const print = async (summary: MessageSummary) => {
    const source = await load(summary);
    if (!source) return;
    printMail({
      subject: source.subject || t('webmail.list.noSubject'),
      rows: [
        [t('webmail.reader.from'), formatAddress(source.from)],
        [t('webmail.reader.to'), source.to.map(formatAddress).join(', ')],
        ...(source.cc.length > 0 ? [[t('webmail.reader.cc'), source.cc.map(formatAddress).join(', ')] as const] : []),
        [t('webmail.reader.date'), dateLabel(source.receivedAt)],
      ],
      html: source.html,
      allowRemote: remoteAllowed.has(source.id),
    });
  };

  const runTool = (tool: MessageTool, summary: MessageSummary) => {
    switch (tool) {
      case 'protectedMail':
        setDialog({ id: summary.id, subject: summary.subject, kind: 'protected' });
        return;
      case 'details':
      case 'viewSource':
        setDialog({ id: summary.id, subject: summary.subject, kind: tool === 'details' ? 'details' : 'source' });
        return;
      case 'downloadEml':
        downloadEml(summary.id);
        return;
      case 'print':
        void print(summary);
    }
  };

  /** Removes messages from the list and selects the one after them (Outlook). */
  const dropFromList = (ids: readonly string[], preserveUnaffectedSelection = false) => {
    const gone = new Set(ids);
    const positions = items.flatMap((item, index) => (gone.has(item.id) ? [index] : []));
    const remaining = items.filter((item) => !gone.has(item.id));
    for (const id of ids) list.remove(id);
    if (preserveUnaffectedSelection && !current?.ids.some(id => gone.has(id))) return;
    const next = remaining[Math.min(positions[0] ?? 0, remaining.length - 1)];
    setSelection(next ? { folderId, ids: [next.id], activeId: next.id, opened: current?.opened } : undefined);
    if (next?.unread) setRead(summariesOf([next.id]), true);
  };

  const remove = async (targets: readonly MessageSummary[]) => {
    const done: string[] = [];
    for (const target of targets) {
      try {
        await api.deleteMessage(target.id);
        done.push(target.id);
      } catch (error) {
        errorText.notify(error);
        break;
      }
    }
    if (done.length === 0) return;
    dropFromList(done);
    toast.success(t('webmail.reader.deletedToast', { count: done.length }));
    reloadFolders();
  };

  const transferViewKey = JSON.stringify([folderId, search, searchRequest]);
  const transferViewRef = useRef<{ key: string; remove: (ids: readonly string[]) => void } | undefined>(undefined);
  useEffect(() => {
    transferViewRef.current = { key: transferViewKey, remove: ids => {
      if (searching) searchList.remove(ids);
      dropFromList(ids, true);
    } };
    return () => { transferViewRef.current = undefined; };
  });
  const file = async (mode: 'move' | 'copy', ids: readonly string[], sourceFolderIds: readonly string[], target: string) => {
    const call = mode === 'move' ? api.moveMessages : api.copyMessages;
    if (!call) return;
    try {
      if (!await transferWithinStore(folderItems, sourceFolderIds, target, () => call(ids, target))) {
        toast.error(t('webmail.folderPicker.sameStoreOnly'));
        return;
      }
    } catch (error) {
      errorText.notify(error);
      return;
    }
    if (mode === 'move' && transferViewRef.current?.key === transferViewKey) transferViewRef.current.remove(ids);
    const folder = folderItems.find((candidate) => candidate.id === target)?.name ?? target;
    toast.success(t(mode === 'move' ? 'webmail.actions.movedToast' : 'webmail.actions.copiedToast', { count: ids.length, folder }));
    reloadFolders();
  };

  const droppingMail = useRef(false);
  useEffect(() => {
    // Resolve the row and its source from the current page, never from untrusted drag data.
    mailDropHandlerRef.current = (id, target, mode) => {
      if (droppingMail.current || !baseVisible.some(item => item.id === id)) return;
      const sources = transferSourceFolders([id], folderId, searching ? searchResult?.items ?? [] : undefined);
      droppingMail.current = true;
      void file(mode, [id], sources, target).finally(() => { droppingMail.current = false; });
    };
    return () => { mailDropHandlerRef.current = undefined; };
  });
  useEffect(() => () => setMailDrag(undefined), [setMailDrag]);

  const registry = createMessageActions(
    {
      open: (summary) => select(summary.id),
      respond: (mode, summary) => void respond(mode, summary),
      setRead,
      setFlag,
      pickFolder: (mode, targets) => {
        const ids = targets.map(messageId);
        setPicker({ mode, ids, sourceFolderIds: transferSourceFolders(ids, folderId, searching ? searchResult?.items ?? [] : undefined) });
      },
      remove,
      tool: runTool,
    },
    {
      canFlag: Boolean(api.setFlag),
      canMove: Boolean(api.moveMessages),
      canCopy: Boolean(api.copyMessages),
      inDeletedItems: folders.find((folder) => folder.id === folderId)?.wellKnown === 'deleted',
    },
  );
  useActionShortcuts(resolveActions(registry, selected, 'shortcut'));
  usePaletteActions(resolveActions(registry, selected, 'palette'));

  const menuTitle = (targets: readonly MessageSummary[]) =>
    targets.length === 1 ? targets[0]!.subject || t('webmail.list.noSubject') : t('webmail.actions.menuTitle', { count: targets.length });
  const contextMenu = (ids: readonly string[]): ContextMenuSpec => {
    const targets = summariesOf(ids);
    return { title: menuTitle(targets), actions: resolveActions(registry, targets, 'context') };
  };
  const toolbar = (variant: 'icon' | 'label') => (
    <>
      <ActionButtons actions={resolveActions(registry, selected, 'toolbar')} variant={variant} />
      <ActionOverflowMenu actions={resolveActions(registry, selected, 'overflow')} title={menuTitle(selected)} />
    </>
  );

  const addressActions = createAddressActions({
    copy: async ({ address }) => {
      try {
        await navigator.clipboard.writeText(address);
        toast.success(t('webmail.addressActions.copiedToast'));
      } catch (error) {
        errorText.notify(error);
      }
    },
    newMessage: (address) => compose({ mode: 'new', initial: { to: toInit([address]) } }),
  });

  const nestedMessage = openAttachment ? attached.data : undefined;
  const readingMessage: ReadingPaneMessage | undefined = openAttachment
    ? nestedMessage && {
        subject: nestedMessage.subject,
        from: person(nestedMessage.from),
        to: nestedMessage.to.map(person),
        cc: nestedMessage.cc.map(person),
        dateLabel: dateLabel(nestedMessage.sentAt),
        html: nestedMessage.html,
      }
    : open && {
        subject: open.subject,
        from: person(open.from),
        to: open.to.map(person),
        cc: open.cc.map(person),
        dateLabel: dateLabel(open.receivedAt),
        html: open.html,
      };
  // Remote images: per message; the nested message has its own key.
  const remoteKey = openAttachment ? `${openAttachment.messageId}/${openAttachment.attachmentId}` : readingId;

  // Files of the nested message are listed only; attached messages of the open one open nested.
  const attachmentItems: AttachmentStripItem[] = openAttachment
    ? (nestedMessage?.attachments ?? []).map((a, index) => ({ id: `n-${index}`, name: a.name, kind: 'file' as const, sizeLabel: formatBytes(a.size, formatNumber) }))
    : (open?.attachments ?? []).map((a) => ({ id: a.id, name: a.name, kind: a.kind, sizeLabel: a.size === null ? undefined : formatBytes(a.size, formatNumber) }));
  const openNested = openAttachment || !open ? undefined : (attachmentId: string) => setNested({ messageId: open.id, attachmentId });
  const downloadUrl = api.attachmentDownloadUrl;
  // Files of the open message download from the API; the nested message's files are listed only.
  const download = downloadUrl && open && !openAttachment ? (attachmentId: string) => startDownload(downloadUrl(open.id, attachmentId)) : undefined;
  const openStripItem = (id: string, kind: 'file' | 'item') => (kind === 'item' ? openNested?.(id) : download?.(id));
  const attachmentActions = createAttachmentActions(
    {
      open: (attachment) => openStripItem(attachment.id, attachment.kind),
      download: (attachment) => download?.(attachment.id),
      saveAll: () => {
        for (const item of attachmentItems) download?.(item.id);
      },
    },
    { canDownload: download !== undefined, count: attachmentItems.length },
  );
  const attachmentMenu = (id: string): ContextMenuSpec | undefined => {
    const attachment = attachmentItems.find((candidate) => candidate.id === id);
    if (!attachment || (attachment.kind === 'item' && !openNested)) return undefined;
    return { title: attachment.name, actions: resolveActions(attachmentActions, [attachment], 'context') };
  };

  const phoneReading = Boolean(readingId && current?.opened);
  // Phones: the composer takes the whole screen (fixed), the list is not rendered under it.
  const composing = composer !== undefined && composer !== null && composer !== false;
  const showList = isDesktop || (!phoneReading && !composing);
  const showReader = isDesktop || phoneReading || composing;
  // Desktop: a composer in the reading pane replaces the message view (OWA); full width/docked lie over it.
  const readerHidden = composing && (!isDesktop || composerLayout === 'pane');

  return (
    <div className="flex h-full min-h-0 min-w-0">
      <SearchFilterDialog open={searchPanelOpen} onOpenChange={setSearchPanelOpen} draft={searchDraft}
        fallbackFocusRef={searchFallbackFocus}
        onDraftChange={setDraft} errors={searchDraftErrors} onApply={applySearchDraft}
        onClear={() => { setSearchFilter({}); resetSearchDraft({}); }} />
      {showList && (
        <PaneContent className={cn('flex min-h-0 min-w-0 flex-col border-r', isDesktop ? 'shrink-0' : 'flex-1')} width={isDesktop ? mailListPane.width : undefined}>
          <div className="flex h-bar shrink-0 items-center justify-end border-b px-3">
            <Button variant="ghost" size="sm" disabled={list.busy || searchLoading || (searching && !searchResult && !searchFailed)}
              onClick={() => { void refreshMail(); }}>
              <RefreshCwIcon className={cn((searching ? searchList.refreshing : list.refreshing) && 'animate-spin')} aria-hidden="true" />
              {t((searching ? searchList.refreshing : list.refreshing) ? 'webmail.mail.refreshing' : 'webmail.mail.refresh')}
            </Button>
          </div>
          {searching && (
            <SearchRefine
              scope={searchScope}
              onScopeChange={setSearchScope}
              filter={searchFilter}
              onFilterChange={setSearchFilter}
              panelOpen={searchPanelOpen}
              onTogglePanel={() => { resetSearchDraft(searchFilter); setSearchPanelOpen(!searchPanelOpen); }}
              // The query itself is in the search bar; the row lists only the other active filters.
              chips={activeFilterChips.filter((chip) => chip.key !== 'query' && !QUICK_FILTER_KEYS.has(chip.key))}
              onRemoveChip={removeSearchFilter}
            />
          )}
          {searching && searchResult && (!searchResult.complete || searchResult.skipped.length > 0) && (
            <div aria-live="polite" className="border-b px-3 py-2 text-xs text-muted-foreground">
              {searchResult.skipped.length > 0 ? t('webmail.searchSkipped') : t('webmail.searchIncomplete')}
              {searchCursor && (
                <Button className="ml-2 h-auto px-1 py-0 text-xs" variant="link" onClick={loadMoreSearch} disabled={searchLoading}>
                  {t('webmail.searchContinue')}
                </Button>
              )}
            </div>
          )}
          <MailListPane
            className="min-h-0 flex-1"
            items={items}
            activeId={current?.activeId}
            onSelect={select}
            selectedIds={selectedIds}
            onSelectionChange={changeSelection}
            contextMenu={contextMenu}
            onEndReached={searching ? (searchCursor ? loadMoreSearch : undefined) : list.hasMore ? list.loadMore : undefined}
            loadingMore={searching ? searchLoading && Boolean(searchResult) : list.loadingMore}
            loading={searching ? (!searchResult && !searchFailed) || (searchLoading && !searchResult) : list.loading}
            error={searching ? searchFailed && !searchResult : Boolean(list.error)}
            onRetry={() => { void refreshMail(); }}
            draggableRows={isDesktop}
            onDragMailItem={(item) => setMailDrag(item && !droppingMail.current ? { item, sourceFolderIds: transferSourceFolders([item.id], folderId, searching ? searchResult?.items ?? [] : undefined) } : undefined)}
            highlight={highlighter}
            labels={{
              list: t('webmail.list.label'),
              emptyTitle: t(searchHasNoMatches ? 'webmail.list.searchEmptyTitle' : 'webmail.list.emptyTitle'),
              emptyDescription: t(searchHasNoMatches ? 'webmail.list.searchEmptyDescription' : 'webmail.list.emptyDescription'),
              noSubject: t('webmail.list.noSubject'),
              hasAttachments: t('webmail.list.hasAttachments'),
              flagged: t('webmail.list.flagged'),
              unread: t('webmail.list.unread'),
              loadingMore: t('webmail.list.loadingMore'),
            }}
          />
          {searching && <SearchIndexFooter count={searchResult?.unindexable ?? 0} />}
        </PaneContent>
      )}
      {isDesktop && showList && showReader && <PaneResizeHandle pane={mailListPane} label={t('webmail.list.label')} />}
      {showReader && (
        <div className="flex min-w-0 flex-1 flex-col">
          {/* One position for every composer layout: switching pane/full width/docked keeps what was typed. */}
          {composer}
          {!isDesktop && !readerHidden && (
            <div className="flex h-bar shrink-0 items-center border-b px-2">
              <Button variant="ghost" onClick={() => setSelection(undefined)}>
                <ArrowLeftIcon />
                {t('webmail.list.label')}
              </Button>
            </div>
          )}
          {readerHidden ? null : selected.length > 1 ? (
            // Multi-selection: the bulk bar is the path to the actions without right-click (T10).
            <EmptyState
              className="flex-1"
              icon={ListChecksIcon}
              title={t('webmail.actions.selectionTitle', { count: selected.length })}
              description={t('webmail.actions.selectionDescription')}
              action={<div className="flex flex-wrap items-center justify-center gap-2">{toolbar('label')}</div>}
            />
          ) : (
            <ReadingPane
              className="min-h-0 flex-1"
              message={readingMessage}
              loading={Boolean(readingId) && (openAttachment ? !nestedMessage && !attached.error : !open && !message.error)}
              error={Boolean(openAttachment ? attached.error : message.error)}
              onRetry={openAttachment ? attached.reload : message.reload}
              highlight={openAttachment ? undefined : readerHighlighter}
              allowRemote={remoteKey ? remoteAllowed.has(remoteKey) : false}
              onAllowRemote={() => remoteKey && setRemoteAllowed((currentSet) => new Set(currentSet).add(remoteKey))}
              banner={
                openAttachment && (
                  <div className="flex h-bar shrink-0 items-center gap-2 border-b px-2 text-sm">
                    <Button variant="ghost" size="sm" onClick={() => setNested(undefined)}>
                      <ArrowLeftIcon />
                      {t('webmail.reader.backToMessage')}
                    </Button>
                    <span className="text-muted-foreground">{t('webmail.reader.attachedMessage')}</span>
                  </div>
                )
              }
              attachments={
                <>
                  {!openAttachment && open?.meeting && <div className="p-3"><MeetingCard messageId={open.id} meeting={open.meeting} onAnswered={message.reload} /></div>}
                  <AttachmentStrip
                    items={attachmentItems}
                    labels={{ list: t('webmail.reader.attachments'), open: t('webmail.reader.openAttachedMessage'), download: t('webmail.reader.downloadAttachment') }}
                    onOpen={openNested || download ? openStripItem : undefined}
                    contextMenu={attachmentMenu}
                  />
                </>
              }
              // The nested message is read-only: its actions belong to the outer message.
              actions={openAttachment ? undefined : toolbar('icon')}
              addressMenu={(entry) => ({ title: entry.name || entry.address, actions: resolveActions(addressActions, [entry], 'context') })}
              labels={{
                emptyTitle: t('webmail.reader.noSelectionTitle'),
                emptyDescription: t('webmail.reader.noSelectionDescription'),
                from: t('webmail.reader.from'),
                to: t('webmail.reader.to'),
                cc: t('webmail.reader.cc'),
                noSubject: t('webmail.list.noSubject'),
                remoteBlocked: t('webmail.reader.remoteBlocked'),
                showImages: t('webmail.reader.showImages'),
                content: t('webmail.reader.contentLabel'),
              }}
            />
          )}
        </div>
      )}
      {picker && (
        <FolderPickerDialog
          open
          onOpenChange={(value) => !value && setPicker(undefined)}
          folders={transferPlan.folders}
          disabledIds={transferPlan.disabledIds}
          pickerLabels={entityPickerLabelsFor(t)}
          onPick={(target) => void file(picker.mode, picker.ids, picker.sourceFolderIds, target)}
          labels={{
            title: t(picker.mode === 'move' ? 'webmail.folderPicker.moveTitle' : 'webmail.folderPicker.copyTitle'),
            search: t('webmail.folderPicker.search'),
            empty: t('webmail.folderPicker.empty'),
            description: t('webmail.folderPicker.sameStoreOnly'),
          }}
        />
      )}
      {dialog && <MessageDialogs messageId={dialog.id} subject={dialog.subject} open={dialog.kind} onClose={() => setDialog(undefined)} />}
    </div>
  );
}
